<script setup>
import { AnnStore } from "@/front/stores/ann_store.js";
import { PlayerStore } from "@/front/stores/player_store.js";
import { ClickStore } from "@/front/stores/click_store.js";
import { DesignStore } from "@/front/stores/design_store.js";
import { PlaybackStore } from "@/front/stores/playback_store.js";
import { VideoStore } from "@/front/stores/video_store.js";
import { LeadCaptureStore } from "@/front/stores/lead_capture_store.js";
const videoStore = VideoStore();
import {
  clickInteraction,
  doAction,
  doVariableEvaluation,
} from "@/front/composables/ClickComposer.js";
import { PositionCalculator } from "@/front/composables/PositionCalculator.js";

const { getRelativeLayerDimensions } = PositionCalculator();
const playerStore = PlayerStore();
const clickStore = ClickStore();
const annStore = AnnStore();
const designStore = DesignStore();
const playbackStore = PlaybackStore();
const leadCaptureStore = LeadCaptureStore();
import { getBaseButtonStyle } from "@/front/composables/StyleComposer.js";
import { equalsIgnoreCase } from "@/front/composables/StringComposer";
const DatePicker = defineAsyncComponent(() => import("primevue/datepicker"));
const Select = defineAsyncComponent(() => import("primevue/select"));
const InputNumber = defineAsyncComponent(() => import("primevue/inputnumber"));
const Checkbox = defineAsyncComponent(() => import("primevue/checkbox"));
const QuestionCorrectness = defineAsyncComponent(() =>
  import("@/front/components/QuestionCorrectness.vue")
);

const promptColor = ref("white");
import Button from "primevue/button";
import TextArea from "primevue/textarea";
import {
  ArrowRight02Icon,
  ArrowLeft02Icon,
  StarIcon,
  ViewIcon,
} from "hugeicons-vue";
import { compile } from "vue";
const { t } = useI18n({});
import { isNotEmpty } from "@/front/composables/GeneralHelper";

const VideoRecorder = defineAsyncComponent(() =>
  import("@/front/components/VideoRecorder.vue")
);
const AudioRecorder = defineAsyncComponent(() =>
  import("@/front/components/AudioRecorder.vue")
);
const ImageResponse = defineAsyncComponent(() =>
  import("@/front/components/ImageResponse.vue")
);
const DrawCreationLayer = defineAsyncComponent(() =>
  import("@/front/components/DrawCreationLayer.vue")
);
const BarChart = defineAsyncComponent(() =>
  import("@/front/components/BarChart.vue")
);
import { dmyyyy } from "@/front/composables/DateComposer";

import { localize } from "@/front/composables/StringComposer";

const props = defineProps({
  readonly: {
    type: Boolean,
    default: false,
  },
});
// Have to calcualte aspect ratio to pin layer to actual video

const getStyles = computed(() => {

  let styles = {};

  const useEditValues =
    annStore.editAnn && annStore.editAnn.question_use_custom_design;
  const useActiveValues =
    !useEditValues &&
    clickStore.questionActive &&
    clickStore.questionActive.question_use_custom_design;

  // log.info(3,"💟 QuestionLayer getStyles useActive / useEdit", useActiveValues, useEditValues)

  var left = useEditValues
    ? annStore.editAnn.pos_left
    : useActiveValues
    ? clickStore.questionActive.pos_left
    : videoStore.video.design.question_left;
  var top = useEditValues
    ? annStore.editAnn.pos_top
    : useActiveValues
    ? clickStore.questionActive.pos_top
    : videoStore.video.design.question_top;
  var width = useEditValues
    ? annStore.editAnn.pos_width
    : useActiveValues
    ? clickStore.questionActive.pos_width
    : videoStore.video.design.question_width;
  var height = useEditValues
    ? annStore.editAnn.pos_height
    : useActiveValues
    ? clickStore.questionActive.pos_height
    : videoStore.video.design.question_height;
  const backgroundColor = useEditValues
    ? annStore.editAnn.background_color
    : useActiveValues
    ? clickStore.questionActive.background_color
    : videoStore.video.design.question_background_color;
  const textColor = useEditValues
    ? annStore.editAnn.text_color
    : useActiveValues
    ? clickStore.questionActive.text_color
    : videoStore.video.design.prompt_color;
  const correctColor = useEditValues
    ? annStore.editAnn.question_correct_color
    : useActiveValues
    ? clickStore.questionActive.question_correct_color
    : videoStore.video.design.correct_color;
  const incorrectColor = useEditValues
    ? annStore.editAnn.question_incorrect_color
    : useActiveValues
    ? clickStore.questionActive.question_incorrect_color
    : videoStore.video.design.incorrect_color;
  const answersColor = useEditValues
    ? annStore.editAnn.answers_color
    : useActiveValues
    ? clickStore.questionActive.answers_color
    : videoStore.video.design.question_answers_color;


  const desiredLeft = left;
  const desiredTop = top;
  const desiredWidth = width;
  const desiredHeight = height;

  // If small device, use full area
  // Don't use full area if fullscreen mobile flag is not true
  if (playerStore.isMobile && !isOverridingFullscreenMobile.value) {
    log.info(3,"💟 QuestionLayer getStyles use_fullscreen_mobile", videoStore.video.design.use_fullscreen_mobile);
    width = 100;
    height = 100;
    left = 0;
    top = 0;
  }

  // styles = getRelativeLayerDimensions(top, left, width, height);
  styles.top = top + "%";
  styles.left = left + "%";
  styles.width = width + "%";
  styles.height = height + "%";
  styles.background = answeringDrawing.value ? "transparent" : backgroundColor;
  styles.color = textColor;

  if (answeringDrawing.value || answeringVideo.value || answeringAudio.value) {
    styles.left = "0";
    styles.top = "0";
    styles.width = "100%";
    styles.height = "100%";
  }

  // Set the prompt color for use within template
  promptColor.value = textColor;

  // Set timeout to find tallest button and match others
  // ONLY if we are not using full width answers
  if (!useFullWidthButtons.value) {
    setTimeout(() => {
      const answerButtons = document.querySelectorAll(".answerButton");
      if (answerButtons.length > 0) {
        answerButtons.forEach((button) => {
          button.style.minHeight = "unset";
        });
        setTimeout(() => {
          let maxHeight = 0;
          answerButtons.forEach((button) => {
            const height = button.offsetHeight;
            if (height > maxHeight) {
              maxHeight = height;
            }
          });
          log.info(3,"💟 QuestionLayer tallestButton", maxHeight);
          answerButtons.forEach((button) => {
            button.style.minHeight = `${maxHeight}px`;
          });
        }, 50);
      }
    }, 50);
  }
  // log.info(3,"💟 QuestionLayer styles", styles)
  return {
    general: styles,
    prompt: {
      color: useEditValues
        ? annStore.editAnn.text_color
        : useActiveValues
        ? clickStore.questionActive.text_color
        : videoStore.video.design.prompt_color,
    },
    correct_answer: {
      color: correctColor,
    },
    incorrect_answer: {
      color: incorrectColor,
    },
    answers: {
      color: answersColor,
    },
    dimensions: {
      left: desiredLeft,
      top: desiredTop,
      width: desiredWidth,
      height: desiredHeight,
    }
  };
});

let correctState = reactive({
  visible: false,
  set: false,
  isCorrect: false,
  correctValue: "",
  answerValue: "",
  action: "",
  value: "",
});

const answerChoices = computed(() => {
  const answers = annStore.editAnn
    ? annStore.editAnn.question_answers
    : clickStore.questionActive.transformed_question_answers || clickStore.questionActive.question_answers;
  return typeof answers === "string" ? JSON.parse(answers) : answers;
});

const handleDrawingSuccess = (image) => {
  log.info(3,"💟 QuestionLayer handleDrawingSuccess", image);
  response.value = image;
  submitDrawing();
};

const leadCaptureActive = () => {
  leadCaptureStore.handleInteractionClick(clickStore.questionActive.id)
  if (clickStore.captureLeadActive) {
    return true
  }
  return false
}

const doSubmit = () => {
  // if (leadCaptureActive()) {
  //   log.info(1,"💟 QuestionLayer doSubmit leadCaptureActive, return");
  //   return
  // }
  // // Check if lead cap is set to first interaction and not already shown
  // if (clickStore.captureLeadActive) {
  //   log.info(1,"💟 QuestionLayer doSubmit captureLeadActive, return");
  //   return
  // }

  log.info(3,"💟 QuestionLayer doSubmit", response.value);
  let t = clickStore.questionActive.question_type;
  if (t == "select") {
    handleAnswerClick(response.value);
  } else {
    submitResponse();
  }
};

const handleRating = (value) => {
  log.info(3,"💟 QuestionLayer handleRating", value);
  if (leadCaptureActive()) {
    log.info(1,"💟 QuestionLayer handleRating leadCaptureActive, return");
    return
  }
  response.value = value;
  submitResponse();
};

const handleLikert = (value) => {
  log.info(3,"💟 QuestionLayer handleLikert", value);
  // if (leadCaptureActive()) {
  //   log.info(1,"💟 QuestionLayer handleLikert leadCaptureActive, return");
  //   return
  // }
  response.value = value;
  submitResponse();
};

const submitDrawing = () => {
  log.info(3,"questionLayer submitDrawing", response.value);
  if (annStore.isEditing) {
    log.info(3,"💟 QuestionLayer submitDrawing annStore.isEditing, return");
    return;
  }
  if (leadCaptureActive()) {
    log.info(1,"💟 QuestionLayer submitDrawing leadCaptureActive, return");
    return
  }
  playbackStore
    .safeTrackDrawingResponse(
      videoStore.video,
      clickStore.questionActive,
      response.value
    )
    .then(() => {
      response.value = null;
      answeringDrawing.value = false;
      clickStore.stopDrawing();
      tryContinueQuestion();
    });
};

const submitResponse = () => {
  log.info(3,"questionLayer submitResponse", response.value);
  log.info(3,
    "💟 QuestionLayer submitResponse clickStore.questionCorrectness",
    JSON.parse(JSON.stringify(clickStore.questionCorrectness))
  );
  if (annStore.isEditing) {
    log.info(3,"💟 QuestionLayer submitResponse annStore.isEditing, return");
    return;
  }
  // LATER why were we localizing answers here?
  if (clickStore.questionActive.question_type == "all") {
    response.value = response.value.sort().join(", ");
    log.info(3,"💟 QuestionLayer submitResponse all sorted", response.value);
  }
  clickStore.clearTimedPause();
  switch (clickStore.questionActive.question_type) {
    case "free":
      handleMultiCorrectness(response.value);
      break;
    case "number":
      handleNumberCorrectness(response.value);
      break;
    case "date":
      response.value = dmyyyy(response.value);
      handleSingleCorrectness({
        text: response.value,
        click_action: "",
        click_value: "",
      });
      break;
    case "all":
      handleAllCorrectness(response.value);
      break;
    case "select":
      // handleSingleCorrectness(response.value);
      break;
    case "poll":
      handlePoll(response.value);
      break;
  }
  handleVariables(response.value);
  playbackStore.safeTrackResponse(
    videoStore.video,
    clickStore.questionActive,
    response.value.toString(),
    clickStore.questionCorrectness
  );
  log.info(3,
    "💟 QuestionLayer submitResponse clickStore.questionCorrectness action / value / visible",
    clickStore.questionCorrectness.action,
    clickStore.questionCorrectness.value,
    clickStore.questionCorrectness.visible
  );

  let shouldDoAction = Boolean(
    clickStore.questionCorrectness.action &&
      isNotEmpty(clickStore.questionCorrectness.value) &&
      !(
        clickStore.questionCorrectness.action == "message" &&
        clickStore.questionCorrectness.visible
      )
  );
  log.info(3,
    "💟 QuestionLayer submitResponse shouldDoAction: " + shouldDoAction
  );
  if (shouldDoAction) {
    log.info(3,
      "💟 QuestionLayer submitResponse action",
      clickStore.questionCorrectness.action,
      clickStore.questionCorrectness.value,
      clickStore.questionCorrectness.click_time
    );
    doAction({
      click_action: clickStore.questionCorrectness.action,
      click_value: clickStore.questionCorrectness.value,
      click_time: clickStore.questionCorrectness.click_time,
    });
  } else if (!clickStore.questionCorrectness.visible) {
    log.info(3,
      "💟 QuestionLayer submitResponse no action, tryContinueQuestion"
    );
    tryContinueQuestion();
  }
  response.value = null;
  // LATER this was causing a skip
  // tryContinueQuestion()
};

const isOverridingFullscreenMobile = computed(() => {
  if (videoStore.video.design.use_fullscreen_mobile || !playerStore.isMobile) {
    return false
  }
  // Check the actual dimensions that would be used
  const useEditValues =
    annStore.editAnn && annStore.editAnn.question_use_custom_design;
  const useActiveValues =
    !useEditValues &&
    clickStore.questionActive &&
    clickStore.questionActive.question_use_custom_design;

  const width = useEditValues
    ? annStore.editAnn.pos_width
    : useActiveValues
    ? clickStore.questionActive.pos_width
    : videoStore.video.design.question_width;
  const height = useEditValues
    ? annStore.editAnn.pos_height
    : useActiveValues
    ? clickStore.questionActive.pos_height
    : videoStore.video.design.question_height;
  const left = useEditValues
    ? annStore.editAnn.pos_left
    : useActiveValues
    ? clickStore.questionActive.pos_left
    : videoStore.video.design.question_left;
  const top = useEditValues
    ? annStore.editAnn.pos_top
    : useActiveValues
    ? clickStore.questionActive.pos_top
    : videoStore.video.design.question_top;

  let result = !(width == 100 && height == 100 && left == 0 && top == 0) 
  log.info(3, "💟 QuestionLayer isOverridingFullscreenMobile", result, { width, height, left, top });
  // If mobile and fullscreen mobile flag is not true and dimensions are not fullscreen already, then we are overriding fullscreen mobile  
  return result
});

const prompt = computed(() => {
  return (
    clickStore.questionActive.transformed_question_prompt ||
    clickStore.questionActive.question_prompt ||
    annStore.editAnn?.question_prompt
  );
});

const stripHtml = (value) => {
  if (!value) return "";
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

const promptText = computed(() => {
  return stripHtml(prompt.value || localize("enter_prompt"));
});

const promptId = computed(() => {
  return `question-prompt-${clickStore.questionActive?.id || "unknown"}`;
});

const livePromptMessage = ref("");

const likertOptions = [
  { label: localize("strongly_disagree"), value: "Strongly Disagree" },
  { label: localize("disagree"), value: "Disagree" },
  { label: localize("neutral"), value: "Neutral" },
  { label: localize("agree"), value: "Agree" },
  { label: localize("strongly_agree"), value: "Strongly Agree" },
];

const showSubmitButton = computed(() => {
  return ["free", "all", "select", "number", "date", "rating"].includes(
    clickStore.questionActive.question_type
  );
});

const canSubmit = computed(() => {
  return response.value != null && response.value.toString().trim().length > 0;
});

const answeringVideo = ref(false);
const answeringAudio = ref(false);
const answeringDrawing = ref(false);

const startAnswerVideo = () => {
  log.info(3,"💟 QuestionLayer startAnswerVideo");
  answeringVideo.value = true;
};

const startAnswerAudio = () => {
  log.info(3,"💟 QuestionLayer startAnswerAudio");
  answeringAudio.value = true;
};

const startAnswerDrawing = () => {
  log.info(3,"💟 QuestionLayer startAnswerDrawing");
  answeringDrawing.value = true;
  clickStore.startDrawing();
};

const handleMultiCorrectness = (answer) => {
  clickStore.resetQuestionCorrectness();
  const correctAnswers = clickStore.questionActive.question_multi_correct;
  if (!correctAnswers || correctAnswers.length == 0) {
    clickStore.questionCorrectness.visible = false;
    return;
  }
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness correctAnswers / answer",
    correctAnswers,
    answer
  );

  const isCorrect = !!correctAnswers.find((correctAnswer) =>
    equalsIgnoreCase(answer, correctAnswer)
  );
  log.info(3,"💟 QuestionLayer handleMultiCorrectness isCorrect", isCorrect);

  clickStore.questionCorrectness.correctValue = correctAnswers.join(", ");
  clickStore.questionCorrectness.isCorrect = isCorrect;
  clickStore.questionCorrectness.answerValue = answer;
  clickStore.questionCorrectness.force = clickStore.isForceCorrect;
  
  clickStore.questionCorrectness.visible =
    videoStore.video.design.question_correctness;
    
  clickStore.questionCorrectness.action = isCorrect
    ? clickStore.questionActive.question_correct_action
    : clickStore.questionActive.question_incorrect_action;

  clickStore.questionCorrectness.value = isCorrect
    ? clickStore.questionActive.question_correct_value
    : clickStore.questionActive.question_incorrect_value;

  clickStore.questionCorrectness.action = isCorrect
    ? clickStore.questionActive.question_correct_action
    : clickStore.questionActive.question_incorrect_action;

    clickStore.questionCorrectness.click_time = isCorrect
    ? clickStore.questionActive.question_correct_arg
    : clickStore.questionActive.question_incorrect_arg;

    clickStore.questionCorrectness.set = true;


  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness isCorrect",
    isCorrect
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness visible",
    clickStore.questionCorrectness.visible
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness action",
    clickStore.questionCorrectness.action
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness value",
    clickStore.questionCorrectness.value
  );
};

const handleNumberCorrectness = (answer) => {
  clickStore.resetQuestionCorrectness();
  const correctAnswers = clickStore.questionActive.question_multi_correct;
  if (!correctAnswers || correctAnswers.length == 0) {
    clickStore.questionCorrectness.visible = false;
    return;
  }
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness correctAnswers / answer",
    correctAnswers,
    answer
  );
  let minVal = correctAnswers[0];
  let maxVal = correctAnswers[1];

  const isCorrect = answer >= minVal && (!maxVal || answer <= maxVal);
  log.info(3,"💟 QuestionLayer handleMultiCorrectness isCorrect", isCorrect);

  clickStore.questionCorrectness.correctValue =
    minVal == maxVal ? minVal : `${minVal} - ${maxVal}`;
  clickStore.questionCorrectness.isCorrect = isCorrect;
  clickStore.questionCorrectness.answerValue = answer;
  clickStore.questionCorrectness.force = clickStore.isForceCorrect;
  clickStore.questionCorrectness.visible =
    videoStore.video.design.question_correctness;
  clickStore.questionCorrectness.action = isCorrect
    ? clickStore.questionActive.question_correct_action
    : clickStore.questionActive.question_incorrect_action;
  clickStore.questionCorrectness.value = isCorrect
    ? clickStore.questionActive.question_correct_value
    : clickStore.questionActive.question_incorrect_value;
  clickStore.questionCorrectness.set = true;

  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness isCorrect",
    isCorrect
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness visible",
    clickStore.questionCorrectness.visible
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness action",
    clickStore.questionCorrectness.action
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness value",
    clickStore.questionCorrectness.value
  );
};

const handleAllCorrectness = (answer) => {
  clickStore.resetQuestionCorrectness();
  var correctAnswers = false;
  log.info(3,
    "💟 QuestionLayer handleAllCorrectness correctAnswers",
    clickStore.questionActive.question_multi_correct,
    typeof clickStore.questionActive.question_multi_correct
  );
  try {
    correctAnswers = JSON.parse(
      clickStore.questionActive.question_multi_correct
    );
    log.info(3,
      "💟 QuestionLayer handleAllCorrectness able to parse json",
      correctAnswers
    );
  } catch (e) {
    log.info(3,"💟 QuestionLayer handleAllCorrectness error parsing json", e);
    correctAnswers = clickStore.questionActive.question_multi_correct;
    log.info(3,"💟 QuestionLayer handleAllCorrectness typeof correctAnswers", typeof correctAnswers);
    window.correctAnswers = correctAnswers.value;
    log.info(3,"💟 QuestionLayer handleAllCorrectness correctAnswers", Object.values(correctAnswers));
    if (typeof correctAnswers == "Array") {
      correctAnswers = correctAnswers.sort()
    } else if (typeof correctAnswers == "object") {
      correctAnswers = Object.values(correctAnswers).sort()
    }
  }
  if (!correctAnswers || correctAnswers.length == 0) {
    clickStore.questionCorrectness.visible = false;
    return;
  }
  log.info(3,
    "💟 QuestionLayer handleAllCorrectness correctAnswers / answer",
    correctAnswers,
    answer
  );
  let answer_options = clickStore.questionActive.question_answers.map(answer => answer.text);
  correctAnswers = correctAnswers.filter(answer => answer_options.includes(answer));

  const isCorrect = answer == correctAnswers.join(", ");
  log.info(3,"💟 QuestionLayer handleAllCorrectness isCorrect", isCorrect);

  clickStore.questionCorrectness.correctValue = correctAnswers.join(", ");
  clickStore.questionCorrectness.isCorrect = isCorrect;
  clickStore.questionCorrectness.answerValue = answer; // was previously joining the array
  clickStore.questionCorrectness.force = clickStore.isForceCorrect;
  clickStore.questionCorrectness.visible =
    videoStore.video.design.question_correctness;
  clickStore.questionCorrectness.action = isCorrect
    ? clickStore.questionActive.question_correct_action
    : clickStore.questionActive.question_incorrect_action;
  clickStore.questionCorrectness.value = isCorrect
    ? clickStore.questionActive.question_correct_value
    : clickStore.questionActive.question_incorrect_value;
  clickStore.questionCorrectness.set = true;

  log.info(3,
    "💟 QuestionLayer handleAllCorrectness clickStore.questionCorrectness isCorrect",
    isCorrect
  );
  log.info(3,
    "💟 QuestionLayer handleAllCorrectness clickStore.questionCorrectness visible",
    clickStore.questionCorrectness.visible
  );
  log.info(3,
    "💟 QuestionLayer handleAllCorrectness clickStore.questionCorrectness action",
    clickStore.questionCorrectness.action
  );
  log.info(3,
    "💟 QuestionLayer handleMultiCorrectness clickStore.questionCorrectness value",
    clickStore.questionCorrectness.value
  );
};

const handlePoll = (chosenAnswer) => {
  log.info(3,"💟 QuestionLayer handlePoll", chosenAnswer);
  clickStore.questionActive.question_answers.forEach((answer) => {
    if (answer.text == chosenAnswer.text) {
      answer.count = (answer.count || 0) + 1;
    }
  });
  clickStore.pollActive = true;
};

// Used for single buttons for multiple choice
const handleSingleCorrectness = (answer) => {
  clickStore.resetQuestionCorrectness();
  log.info(3,
    "💟 QuestionLayer handleSingleCorrectness for " +
      clickStore.questionActive.question_type,
    answer
  );
  let isMultiple = clickStore.questionActive.question_type == "multiple";
  let isSelect = clickStore.questionActive.question_type == "select";
  let isDate = clickStore.questionActive.question_type == "date";
  let correctAnswerValue = clickStore.questionActive.question_single_correct;
  // Check correctness
  if (correctAnswerValue) {
    log.info(3,
      "💟 QuestionLayer handleSingleCorrectness correct_answer_value",
      correctAnswerValue
    );

    if (isMultiple || isSelect) {
      log.info(3,
        "💟 QuestionLayer handleAnswerClick multiple/select correct: " +
          answer.text +
          " == " +
          correctAnswerValue
      );
      clickStore.questionCorrectness.isCorrect = equalsIgnoreCase(
        answer.text,
        correctAnswerValue
      );
    } else if (isDate) {
      clickStore.questionCorrectness.isCorrect = equalsIgnoreCase(
        dmyyyy(answer.text),
        correctAnswerValue
      );
    } else {
      log.info(3,"💟 QuestionLayer handleAnswerClick incorrect");
    }
    // Set values for display
    clickStore.questionCorrectness.correctValue = correctAnswerValue;
    clickStore.questionCorrectness.answerValue = answer.text;
    clickStore.questionCorrectness.action = answer.click_action
      ? answer.click_action
      : clickStore.questionCorrectness.isCorrect
      ? clickStore.questionActive.question_correct_action
      : clickStore.questionActive.question_incorrect_action;
    clickStore.questionCorrectness.value = answer.click_value
      ? answer.click_value
      : clickStore.questionCorrectness.isCorrect
      ? clickStore.questionActive.question_correct_value
      : clickStore.questionActive.question_incorrect_value;
    clickStore.questionCorrectness.force = clickStore.isForceCorrect;
    clickStore.questionCorrectness.visible =
      videoStore.video.design.question_correctness;
    clickStore.questionCorrectness.set = true;
  }
  log.info(3,
    "💟 QuestionLayer handleSingleCorrectness clickStore.questionCorrectness / design",
    clickStore.questionCorrectness,
    videoStore.video.design.question_correctness
  );
};

const handleAnswerClick = (answer) => {
  // if (leadCaptureActive()) {
  //   log.info(1,"💟 QuestionLayer handleAnswerClick leadCaptureActive, return");
  //   return
  // }

  log.info(3,"💟 QuestionLayer handleAnswerClick", answer);
  log.info(3,JSON.parse(JSON.stringify(answer)));

  const id = clickStore.questionActive?.id;
  handleSingleCorrectness(answer);
  // handleVariables(answer.text);
  log.info(3,
    "💟 QuestionLayer handleAnswerClick clickStore.questionActive?.question_type",
    clickStore.questionActive?.question_type
  );
  if (clickStore.questionActive?.question_type == "poll") {
    handlePoll(answer);
  }
  log.info(3,
    "💟 QuestionLayer handleAnswerClick visible / message",
    clickStore.questionCorrectness.visible,
    answer.click_action == "message"
  );
  const shouldPerformAction = !(
    clickStore.questionCorrectness.visible && answer.click_action == "message"
  );
  log.info(3,
    "Visible / Message",
    clickStore.questionCorrectness.visible,
    answer.click_action == "message"
  );
  // const shouldPerformAction = answer.click_action != 'message'
  log.info(3,
    "💟 QuestionLayer handleAnswerClick shouldPerformAction",
    shouldPerformAction
  );
  log.info(3,
    "💟 QuestionLayer handleAnswerClick set answer.click_action",
    answer.click_action
  );
  log.info(3,
    "💟 QuestionLayer handleAnswerClick set answer.click_value",
    answer.click_value
  );
  if (["message", "image"].includes(answer.click_action) && answer.second_action) {
    clickStore.startPendingSecondAction(
      answer.second_action,
      answer.second_value,
      answer.second_time
    )
  } else {
    clickStore.clearPendingSecondAction()
  }
  clickStore.questionCorrectness.action = answer.click_action;
  clickStore.questionCorrectness.value = answer.click_value;
  clickStore.questionCorrectness.click_time = answer.click_time;
  log.info(3,
    "💟 QuestionLayer handleAnswerClick clickStore.questionCorrectness.click_time",
    clickStore.questionCorrectness.click_time
  );

  // Set previous video if back
  if (answer.click_action == "back") {
    log.info(3,
      "💟 QuestionLayer handleAnswerClick set previous video: " +
        playbackStore.refer_video
    );
    clickStore.questionCorrectness.value = playbackStore.refer_video;
  }
  response.value = answer.text;
  submitResponse();
};

const tryContinueQuestion = () => {
  log.info(3,
    "💟 QuestionLayer tryContinueQuestion",
    clickStore.questionCorrectness.visible,
    clickStore.questionCorrectness.force,
    clickStore.pollActive
  );
  // Only continue if we are NOT showing force correctness and NOT showing a poll
  if (
    !(
      clickStore.questionCorrectness.visible &&
      clickStore.questionCorrectness.force
    ) &&
    !clickStore.pollActive
  ) {
    log.info(3,"💟 QuestionLayer tryContinueQuestion, finishCurrentQuestion");
    let id = clickStore.questionActive?.id
    annStore.finishCurrentQuestion();
    leadCaptureStore.handleInteractionClick(id)
  } else {
    log.info(3,
      "💟 QuestionLayer tryContinueQuestion, dont finishCurrentQuestion"
    );
  }
};

const handleVariables = (value) => {
  // Invididiual variable
  if (clickStore.questionActive.variable) {
    log.info(3,
      "💟 QuestionLayer Individual doVariableEvaluation",
      clickStore.questionActive.variable,
      value
    );
    doVariableEvaluation(clickStore.questionActive.variable, value);
  }

  if (
    ["multiple", "select"].includes(clickStore.questionActive.question_type)
  ) {
    clickStore.questionActive.question_answers.forEach((answer) => {
      if (equalsIgnoreCase(answer.text, value) && answer.variable) {
        log.info(3,
          "💟 QuestionLayer Multi/Select doVariableEvaluation",
          answer.variable,
          answer.variable_value
        );
        doVariableEvaluation(answer.variable, answer.variable_value);
      }
    });
  }

  if (clickStore.questionActive.question_type == "all") {
    clickStore.questionActive.question_answers.forEach((answer) => {
      if (value.includes(answer.text) && answer.variable) {
        log.info(3,
          "💟 QuestionLayer All doVariableEvaluation",
          answer.variable,
          answer.variable_value
        );
        doVariableEvaluation(answer.variable, answer.variable_value);
      }
    });
  }
};

const response = ref(null);

// Dont allow for more clicking when playing video or audio clips
const disabledResponses = computed(() => {
  return clickStore.clipActive || clickStore.audioActive;
});

const finishCorrectness = () => {
  log.info(3,
    "💟 QuestionLayer finishCorrectness",
    clickStore.questionCorrectness.isCorrect
  );
  if (clickStore.pendingSecondAction) {
    const { action, value, click_time } = clickStore.pendingSecondAction
    clickStore.clearPendingSecondAction()
    if (action && action !== 'reply') {
      doAction({ click_action: action, click_value: value, click_time })
      if (!(clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect)) {
        annStore.finishCurrentQuestion()
      }
      return
    }
  }
  if (clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect) {
    log.info(3,"💟 QuestionLayer finishCorrectness force");
    clickStore.resetQuestionCorrectness();
  } else {
    log.info(3,"💟 QuestionLayer finishCorrectness finishCurrentQuestion");
    annStore.finishCurrentQuestion();
  }
};

const handleVideoSuccess = (object) => {
  let uploadUrl = object.url;
  log.info(3,"💟 QuestionLayer handleVideoSuccess", uploadUrl);
  playbackStore
    .safeTrackVideoResponse(
      videoStore.video,
      clickStore.questionActive,
      uploadUrl
    )
    .then(() => {
      annStore.finishCurrentQuestion();
      answeringVideo.value = false;
    });
};

const handleAudioSuccess = (object) => {
  let uploadUrl = object.url;
  log.info(3,"💟 QuestionLayer handleAudioSuccess", uploadUrl);
  playbackStore
    .safeTrackAudioResponse(
      videoStore.video,
      clickStore.questionActive,
      uploadUrl
    )
    .then(() => {
      annStore.finishCurrentQuestion();
      answeringAudio.value = false;
    });
};

const handleImageSuccess = (object) => {
  let uploadUrl = object.url;
  log.info(3,"💟 QuestionLayer handleImageSuccess", uploadUrl);
  playbackStore
    .safeTrackImageResponse(
      videoStore.video,
      clickStore.questionActive,
      uploadUrl
    )
    .then(() => {
      annStore.finishCurrentQuestion();
    });
};

const editManage = computed(() => {
  return annStore.isEditing || videoStore.video.manage;
});

const skipQuestion = () => {
  log.info(3,"💟 QuestionLayer skipQuestion");
  clickStore.clearTimedPause();
  clickStore.pollActive = false;
  clickStore.clearPendingSecondAction();
  annStore.finishCurrentQuestion();
};

const rewindQuestion = () => {
  let rewindTime = clickStore.questionActive.question_rewind_time;
  log.info(3,"💟 QuestionLayer rewindQuestion rewindTime", rewindTime);
  if (isNotEmpty(rewindTime)) {
    clickStore.resetAll();
    log.info(3,"💟 QuestionLayer rewindQuestion to " + rewindTime);
    playerStore.seekAndPlay(rewindTime).then(() => {
      log.info(3,"💟 QuestionLayer rewindQuestion seekAndPlay", rewindTime);
    });
  } else {
    log.info(3,"💟 QuestionLayer rewindQuestion no rewind time");
  }
};

const answeringBlockingQuestion = computed(() => {
  return answeringVideo.value || answeringAudio.value || answeringDrawing.value;
});

const showSkipButton = computed(() => {
  return (
    !answeringBlockingQuestion.value &&
    (clickStore.questionActive.question_skippable || editManage.value)
  );
});

const showRewindButton = computed(() => {
  return (
    !answeringBlockingQuestion.value &&
    clickStore.questionActive.question_rewindable
  );
});

const finishPoll = () => {
  clickStore.pollActive = false;
  annStore.finishCurrentQuestion();
};

const pollHeight = computed(() => {
  return playerStore.layerDimensions.height;
});

const updateResponse = (event) => {
  log.info(3,"💟 QuestionLayer updateResponse", event);
  response.value = event.value;
};

const backspaceResponse = () => {
  log.info(3,"💟 QuestionLayer backspaceResponse", response.value);
  if (response.value == null) return;
  if (response.value.toString().length == 1) {
    response.value = null;
    return;
  }
  response.value = Number(response.value.toString().slice(0, -1));
};

const setRatingValue = (value) => {
  log.info(3,"💟 QuestionLayer setRatingValue", value);
  response.value = value;
};

const textareaRef = ref(null);
const correctnessContinueButton = ref(null);

const focusTextarea = () => {
  log.info(3,"💟 QuestionLayer focusTextarea");
  if (props.readonly) return;

  nextTick(() => {
    if (!textareaRef.value) return;

    // Handle PrimeVue TextArea structure
    if (textareaRef.value.$el) {
      // Try to find the textarea inside the component
      const textarea = textareaRef.value.$el.querySelector("textarea");
      if (textarea) {
        textarea.focus();
        // Position cursor at the end
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      } else {
        // If we can't find a textarea element, focus the component
        textareaRef.value.$el.focus();
      }
    } else {
      // Direct focus
      textareaRef.value.focus();
    }
  });
};

// Focus continue button when correctness is shown
const focusCorrectnessButton = () => {
  nextTick(() => {
    if (correctnessContinueButton.value) {
      if (correctnessContinueButton.value.$el) {
        correctnessContinueButton.value.$el.focus();
      } else {
        correctnessContinueButton.value.focus();
      }
      // Announce to screen reader
      const announcement = clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect 
        ? "Incorrect answer. Press Enter or Space to try again." 
        : "Correct answer. Press Enter or Space to continue.";
      const announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'assertive');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.textContent = announcement;
      document.body.appendChild(announcer);
      setTimeout(() => document.body.removeChild(announcer), 1000);
    }
  });
};

// Watch for correctness visibility changes to focus button
watch(() => clickStore.questionCorrectness.visible, (newVal) => {
  if (newVal) {
    focusCorrectnessButton();
  }
});

watch(
  () => [clickStore.questionActive?.id, promptText.value],
  ([questionId, nextPrompt]) => {
    if (!questionId || !nextPrompt) return;
    livePromptMessage.value = `Question: ${nextPrompt}`;
    log.info(3, "💟 QuestionLayer announce prompt", {
      questionId,
      prompt: nextPrompt,
    });
  },
  { immediate: true }
);

// Create an interval to maintain focus
const focusInterval = ref(null);
onMounted(() => {
  log.info(3,"💟 QuestionLayer onMounted");
  if (props.readonly) return;
  document.activeElement?.blur();

  focusTextarea();

  // Set up focus monitoring
  focusInterval.value = setInterval(() => {
    // Don't steal focus if lead capture is active
    if (clickStore.captureLeadActive) {
      return;
    }
    if (!clickStore.questionActive) {
      clearInterval(focusInterval.value);
      return;
    }
    
    const textareaId = `question-input-${clickStore.questionActive.id}`;
    const hasDocumentFocus =
      document.activeElement &&
      (document.activeElement === textareaRef.value?.$el ||
        document.activeElement === textareaRef.value ||
        document.activeElement.closest(`#${textareaId}`));

    if (!hasDocumentFocus) {
      focusTextarea();
    }
  }, 300); // Check every 300ms
});

// Clean up interval
onUnmounted(() => {
  if (focusInterval.value) {
    clearInterval(focusInterval.value);
  }
});

const showNumbering = computed(() => {
  return videoStore.video.design.show_question_numbering && !annStore.isEditing;
  // return videoStore.video.design.show_question_numbering && !annStore.isEditing && annStore.originalQuestionQueue.length > 1
});

const numberText = computed(() => {
  let questionAnns = annStore.anns.filter(
    (ann) => ann.visible && !ann.hidden && ann.type_of == "question"
  );
  let ids = questionAnns.map((ann) => ann.id);
  return (
    t("common.question") +
    " " +
    (ids.indexOf(clickStore.questionActive.id) + 1) +
    " " +
    t("common.of") +
    " " +
    ids.length
  );
});

const tryEnterSubmit = (event) => {
  log.info(3,"💟 QuestionLayer tryEnterSubmit", canSubmit.value);
  event.preventDefault();
  event.stopPropagation();
  // if (leadCaptureActive()) {
  //   log.info(1,"💟 QuestionLayer tryEnterSubmit leadCaptureActive, return");
  //   return
  // }
  if (canSubmit.value && videoStore.video.design.enter_continue) {
    submitResponse();
  }
};

const useFullWidthButtons = computed(() => {
  return (
    videoStore.video.design.break_answers ||
    (answerChoices.value &&
      answerChoices.value.some(
        (choice) => choice.text && choice.text.length > 80
      ))
  );
});

const stopDrawing = () => {
  log.info(3,"💟 QuestionLayer stopDrawing");
  clickStore.stopDrawing();
  answeringDrawing.value = false;
};

const getAnswerColSpan = (index) => {
  // Base 2 columns wide
  let len = answerChoices.value.length;
  if (useFullWidthButtons.value || len == 1) {
    return "col-span-2";
  }
  if (len == 3 && index == 2) {
    return "col-span-2";
  }

  if (len == 5 && index == 4) {
    return "col-span-2";
  }
  return "col-span-1";
};

const getPromptClassList = computed(() => {
  if (isOverridingFullscreenMobile.value) {
    return 'text-[14px] leading-[1.3] font-bold'
  }  
  
  let str = '@2xs:text-sm @sm:text-base @xl/player:text-lg @2xl/player:text-2xl tracking-wide mb-2 @md/player:my-4'
  if (prompt.value?.length < 300) {
    str = 'font-bold @md/player:text-lg @lg/player:text-2xl @xl/player:text-3xl tracking-wide mb-2 @md/player:my-4'
  }
  if (prompt.value?.length < 100) {
    str = 'font-bold text-sm @sm/player:text-xl @md/player:text-2xl @lg/player:text-3xl @xl/player:text-4xl tracking-wide mb-2 @md/player:my-4'
  }

  return str
})

const getQuestionLayerClassList = computed(() => {
  let str = ''
  if (prompt.value?.length < 300) {
    str += '@xs/player:flex @xs/player:flex-col @xs/player:justify-center @xs/player:items-center py-4'
  }
  if (annStore.isEditing) {
    str += 'pointer-events-none'
  }
  if (clickStore.questionCorrectness.visible) {
    str += 'flex flex-col items-center justify-center'
  }
  return str
})

const getAnswerButtonClassList = computed(() => {
  let str = "answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-2xl p-2 @md/player:p-3 @lg/player:p-4"

  if (isOverridingFullscreenMobile.value) {
    str += ' !text-[12px] !p-1 !leading-[1.2]'
  } else {
    str += ' text-xs'
  }
  return str
})
</script>

<template>
  <div
    id="question-layer"
    class="absolute top-0 left-0 w-full h-full overflow-y-scroll no-scrollbar items-center px-3 py-4 z-[10]"
    :data-class-list="getQuestionLayerClassList"
    :class="getQuestionLayerClassList"
    :style="getStyles.general"
    @click.stop
    role="region"
    :aria-label="`Question ${clickStore.questionActive?.question_type || 'interaction'}`"
  >
    <div class="flex flex-col items-center w-full">
      <div class="w-full max-w-[90%] mx-auto">
        <div class="sr-only" aria-live="polite" aria-atomic="true">
          {{ livePromptMessage }}
        </div>
        <div v-if="answeringVideo" class="w-full max-w-sm mx-auto">
          <Suspense>
            <VideoRecorder
              @close="answeringVideo = false"
              @success="handleVideoSuccess"
            />
          </Suspense>
        </div>
        <div v-else-if="answeringAudio" class="w-full max-w-sm mx-auto">
          <Suspense>
            <AudioRecorder
              @close="answeringAudio = false"
              @success="handleAudioSuccess"
            />
          </Suspense>
        </div>
        <div v-else-if="answeringDrawing" class="w-full max-w-sm mx-auto">
          <Suspense>
            <DrawCreationLayer
              @save="handleDrawingSuccess"
              @close="stopDrawing"
            />
          </Suspense>
        </div>

        <!-- Displaying poll -->
        <div
          v-else-if="clickStore.pollActive"
          class="w-full bg-white h-full absolute top-0 left-0 text-black"
        >
          <Suspense>
            <BarChart
              type="poll"
              class="w-full h-full"
              :height="pollHeight"
              width="100%"
              :download="false"
            />
          </Suspense>
          <div class="w-full text-center absolute bottom-4 left-0">
            <Button
              @click.stop="finishPoll"
              @keydown.enter.prevent="finishPoll"
              @keydown.space.prevent="finishPoll"
              :size="playerStore.isMobile ? 'small' : 'large'"
              :style="getBaseButtonStyle()"
              aria-label="Continue after viewing poll results"
              role="button"
              tabindex="0"
            >
              {{ localize("continue") }}
            </Button>
          </div>
        </div>

        <!-- Displaying correctnesss -->
        <div v-else-if="clickStore.questionCorrectness.visible" class="h-[100%]">
          <QuestionCorrectness :styles="getStyles" />
          <div class="mt-4 w-full text-center" role="region" aria-live="polite">
            <Button
              ref="correctnessContinueButton"
              @click.stop="finishCorrectness"
              class="text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
              :style="getBaseButtonStyle()"
              :aria-label="clickStore.isForceCorrect && !clickStore.questionCorrectness.isCorrect ? 'Try again button' : 'Continue to next question button'"
              role="button"
              tabindex="0"
              @keydown.enter="finishCorrectness"
              @keydown.space.prevent="finishCorrectness"
            >
              {{
                clickStore.isForceCorrect &&
                !clickStore.questionCorrectness.isCorrect
                  ? localize("try_again")
                  : localize("continue")
              }}
            </Button>
          </div>
        </div>

        <!-- Displaying choices -->
        <div v-else class="pb-6">
          <div class="text-center mb-2">
            <div v-if="showNumbering" class="text-sm @md/player:text-base @lg/player:text-lg pb-2">{{ numberText }}</div>
            <p
              :id="promptId"
              :class="getPromptClassList"
              :style="getStyles.prompt"
              v-html="prompt || localize('enter_prompt')"
              :aria-label="promptText"
              :aria-describedby="promptText"
              tabindex="0"
            />
          </div>
          <div
            v-if="
              clickStore.questionActive.question_use_hint &&
              clickStore.questionActive.question_hint
            "
            class="text-center mb-2 text-sm"
            :aria-describedby="promptId"
          >
            <div
              v-if="clickStore.questionActive.showing_hint"
              class="text-white"
              text
              @click.stop="clickStore.questionActive.showing_hint = false"
            >
              {{ clickStore.questionActive.question_hint }}
            </div>
            <button
              v-else
              @click.stop="clickStore.questionActive.showing_hint = true"
              size="small"
              class="text-white items-top mx-auto"
              text
              aria-label="Show hint for this question"
              role="button"
              tabindex="0"
            >
              <div class="flex items-center justify-center">
                <ViewIcon class="size-4 mr-1 text-white" aria-hidden="true" />
                {{ localize("show_hint") }}
              </div>
            </button>
          </div>
          <!-- Free response -->
          <div
            v-if="clickStore.questionActive.question_type == 'free'"
            class="w-full max-w-md mx-auto"
          >
            <TextArea
              v-model="response"
              ref="textareaRef"
              :id="'question-input-' + clickStore.questionActive.id"
              :placeholder="localize('your_answer')"
              :aria-labelledby="promptId"
              :aria-describedby="promptId"
              @keyup.enter="tryEnterSubmit($event)"
              :disabled="props.readonly"
              class="w-full dynamicInputTextSizing"
              rows="1"
              auto-resize
              @focus="focusTextarea"
            />
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'number'"
            class="flex justify-center"
          >
            <input
              type="number"
              v-model="response"
              :input-id="'question-input-' + clickStore.questionActive.id"
              :placeholder="localize('your_answer')"
              :aria-labelledby="promptId"
              class="w-fit !text-center !text-black !mx-auto text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autocomplete="off"
              :autofocus="!props.readonly"
              @keyup.enter="tryEnterSubmit($event)"
            />
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'date'"
            class="flex justify-center max-w-sm mx-auto !text-center"
          >
            <DatePicker
              v-model="response"
              :placeholder="localize('choose_date')"
              class="w-full p-0 !text-center text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl"
              id="question-layer-datepicker"
              :aria-labelledby="promptId"
              dateFormat="mm/dd/yy"
              :autofocus="!props.readonly"
              :manualInput="false"
              @keyup.enter="tryEnterSubmit($event)"
            />
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'all'"
            class="grid grid-cols-2 gap-3 items-top w-full fill-height"
            role="group"
            :aria-labelledby="promptId"
          >
            <div
              v-for="(answer, index) in answerChoices"
              :key="answer.id"
              :class="{
                'col-span-2': useFullWidthButtons,
                'text-right': index % 2 == 0 && !useFullWidthButtons,
                'text-left': index % 2 == 1 && useFullWidthButtons,
              }"
            >
              <div
                v-if="answer.text"
                class="flex items-top"
                :class="{
                  'justify-end': index % 2 == 0 && !useFullWidthButtons,
                  'justify-start': index % 2 == 1 && !useFullWidthButtons,
                  'justify-center': useFullWidthButtons,
                }"
              >
                <label
                  v-if="index % 2 == 0 && !useFullWidthButtons"
                  :for="`checkbox-${answer.id || index}`"
                  class="mr-2 text-xs @md/player:text-sm @lg/player:text-lg @2xl/player:text-xl"
                  >{{ answer.text }}</label
                >
                <Checkbox 
                  :value="answer.text" 
                  v-model="response"
                  :inputId="`checkbox-${answer.id || index}`"
                  :aria-label="`Select ${answer.text}`"
                />
                <label
                  v-if="index % 2 == 1 || useFullWidthButtons"
                  :for="`checkbox-${answer.id || index}`"
                  class="ml-2 text-xs @md/player:text-sm @lg/player:text-lg @2xl/player:text-xl"
                  >{{ answer.text }}</label
                >
              </div>
            </div>
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'select'"
            class="flex justify-center text-center"
          >
            <Select
              v-model="response"
              :options="answerChoices"
              optionLabel="text"
              :filter="answerChoices.length > 6"
              dropdown
              resetFilterOnHide
              autoFilterFocus
              autoOptionFocus
              :appendTo="playerStore.isFullscreen ? '#player-wrapper' : 'body'"
              class="w-full text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl max-w-sm"
              :placeholder="localize('your_answer')"
              :aria-labelledby="promptId"
            />
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'rating'"
            class="max-w-sm mx-auto"
          >
            <div
              class="grid grid-cols-5 items-center justify-center"
              role="group"
              aria-label="Rating selection from 1 to 5 stars"
              :aria-describedby="promptId"
            >
              <div v-for="i in 5" :key="i" class="col-span-1">
                <button
                  @click.stop="setRatingValue(i)"
                  @keydown.enter.prevent="setRatingValue(i)"
                  @keydown.space.prevent="setRatingValue(i)"
                  :aria-label="`Rate ${i} out of 5 stars`"
                  :aria-pressed="response >= i"
                  :aria-describedby="promptId"
                  role="button"
                  :tabindex="0"
                  class="p-1 rounded transition-transform hover:scale-110"
                >
                  <StarIcon
                    class="w-8 h-8 @md/player:w-12 @md/player:h-12 mx-auto"
                    :fill="response >= i ? 'yellow' : 'none'"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'likert'"
            class="grid grid-cols-2 gap-3 max-w-md mx-auto"
          >
            <div
              class="col-span-1"
              :class="{ 'col-span-2': useFullWidthButtons }"
            >
              <button
                @click.stop="handleLikert(localize('strongly_agree'))"
                @keydown.enter.prevent="handleLikert(localize('strongly_agree'))"
                @keydown.space.prevent="handleLikert(localize('strongly_agree'))"
                :style="getBaseButtonStyle()"
                class="answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] text-xs @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
                :disabled="disabledResponses"
                :aria-label="'Likert scale option: Strongly Agree'"
                :aria-describedby="promptId"
                role="button"
                :tabindex="disabledResponses ? '-1' : '0'"
                >{{ localize("strongly_agree") }}</button
              >
            </div>
            <div
              class="col-span-1"
              :class="{ 'col-span-2': useFullWidthButtons }"
            >
              <button
                @click.stop="handleLikert(localize('agree'))"
                @keydown.enter.prevent="handleLikert(localize('agree'))"
                @keydown.space.prevent="handleLikert(localize('agree'))"
                :style="getBaseButtonStyle()"
                class="answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] text-xs @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
                :disabled="disabledResponses"
                :aria-label="'Likert scale option: Agree'"
                :aria-describedby="promptId"
                role="button"
                :tabindex="disabledResponses ? '-1' : '0'"
                >{{ localize("agree") }}</button
              >
            </div>
            <div class="col-span-2">
              <button
                @click.stop="handleLikert(localize('neutral'))"
                @keydown.enter.prevent="handleLikert(localize('neutral'))"
                @keydown.space.prevent="handleLikert(localize('neutral'))"
                :style="getBaseButtonStyle()"
                class="answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] text-xs @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
                :disabled="disabledResponses"
                :aria-label="'Likert scale option: Neutral'"
                :aria-describedby="promptId"
                role="button"
                :tabindex="disabledResponses ? '-1' : '0'"
                >{{ localize("neutral") }}</button
              >
            </div>
            <div
              class="col-span-1"
              :class="{ 'col-span-2': useFullWidthButtons }"
            >
              <button
                @click.stop="handleLikert(localize('disagree'))"
                @keydown.enter.prevent="handleLikert(localize('disagree'))"
                @keydown.space.prevent="handleLikert(localize('disagree'))"
                :style="getBaseButtonStyle()"
                class="answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] text-xs @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
                :disabled="disabledResponses"
                :aria-label="'Likert scale option: Disagree'"
                :aria-describedby="promptId"
                role="button"
                :tabindex="disabledResponses ? '-1' : '0'"
                >{{ localize("disagree") }}</button
              >
            </div>
            <div
              class="col-span-1"
              :class="{ 'col-span-2': useFullWidthButtons }"
            >
              <button
                @click.stop="handleLikert(localize('strongly_disagree'))"
                @keydown.enter.prevent="handleLikert(localize('strongly_disagree'))"
                @keydown.space.prevent="handleLikert(localize('strongly_disagree'))"
                :style="getBaseButtonStyle()"
                class="answerButton w-full tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 active:scale-[90%] text-xs @sm/player:text-sm @md/player:text-base @lg/player:text-lg @2xl/player:text-xl p-2 @md/player:p-4 @lg/player:p-6"
                :disabled="disabledResponses"
                :aria-label="'Likert scale option: Strongly Disagree'"
                :aria-describedby="promptId"
                role="button"
                :tabindex="disabledResponses ? '-1' : '0'"
                >{{ localize("strongly_disagree") }}</button
              >
            </div>
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'video'"
            class="w-full max-w-md mx-auto text-center"
          >
            <Button
              size="large"
              severity="info"
              :style="getBaseButtonStyle()"
              :aria-describedby="promptId"
              @click.stop="startAnswerVideo"
              >{{ localize("record_response") }}</Button
            >
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'voice'"
            class="w-full max-w-md mx-auto text-center"
          >
            <Button
              size="large"
              severity="info"
              :style="getBaseButtonStyle()"
              :aria-describedby="promptId"
              @click.stop="startAnswerAudio"
              >{{ localize("record_response") }}</Button
            >
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'image'"
            class="w-full max-w-md mx-auto text-center"
          >
            <Suspense>
              <ImageResponse
                @success="handleImageSuccess"
                class="w-full h-20 @md/player:h-48 mx-auto"
              />
            </Suspense>
          </div>
          <div
            v-else-if="clickStore.questionActive.question_type == 'draw'"
            class="w-full max-w-md mx-auto text-center"
          >
            <Button
              size="large"
              severity="info"
              :style="getBaseButtonStyle()"
              :aria-describedby="promptId"
              @click.stop="startAnswerDrawing"
              >{{ localize("draw") }}</Button
            >
          </div>
          <div
            v-else-if="
              ['multiple', 'poll'].includes(
                clickStore.questionActive.question_type
              )
            "
            class="w-full max-w-[90%] mx-auto py-1 @sm/player:py-2 @xl/player:py-4 @2xl/player:py-6 @3xl/player:py-8"
          >
            <div class="grid grid-cols-2 gap-2 @sm/player:gap-4 items-center w-full fill-height">
              <div
                v-for="(answer, index) in answerChoices"
                :key="clickStore.questionActive.id + '-' + index"
                class="items-center"
                :class="getAnswerColSpan(index)"
              >
                <div
                  v-if="answer.text"
                  class="flex items-center justify-center"
                >
                  <button
                    :style="getBaseButtonStyle()"
                    :class="getAnswerButtonClassList"
                    @click.stop="handleAnswerClick(answer)"
                    @keydown.enter.prevent="handleAnswerClick(answer)"
                    @keydown.space.prevent="handleAnswerClick(answer)"
                    :disabled="disabledResponses"
                    :aria-label="`Answer option: ${answer.text}`"
                    :aria-describedby="promptId"
                    role="button"
                    :tabindex="disabledResponses ? '-1' : '0'"
                    >{{ answer.text }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="showSubmitButton" class="mt-4 w-full text-center">
            <Button
              class="dynamicInputTextSizing"
              :style="getBaseButtonStyle()"
              :disabled="!canSubmit"
              @click.stop="doSubmit"
              @keydown.enter.prevent="doSubmit"
              @keydown.space.prevent="doSubmit"
              :aria-label="canSubmit ? 'Submit answer and continue' : 'Submit button - Please enter an answer first'"
              :aria-describedby="promptId"
              role="button"
              :tabindex="!canSubmit ? '-1' : '0'"
              >{{ localize("continue") }}</Button
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Skip button -->
    <div
      v-if="showSkipButton"
      class="absolute top-0 right-0 z-10"
      :class="{
        'top-4':
          clickStore.activeTimedPause &&
          videoStore.video?.design.show_countdown_bar,
      }"
    >
      <Button
        size="small"
        text
        @click.stop="skipQuestion"
        v-tooltip.left="
          editManage ? localize('skip_as_owner') : localize('skip')
        "
      >
        <ArrowRight02Icon
          class="inline-block size-4 @md/player:size-6 @lg/player:size-8"
          :style="{ color: promptColor }"
        />
      </Button>
    </div>

    <!-- Rewind button -->
    <div
      v-if="showRewindButton"
      class="absolute top-0 left-0"
      :class="{
        'top-4':
          clickStore.activeTimedPause &&
          videoStore.video?.design.show_countdown_bar,
      }"
    >
      <Button
        size="small"
        text
        @click.stop="rewindQuestion"
        v-tooltip.right="localize('rewind')"
      >
        <ArrowLeft02Icon
          class="inline-block size-8 mr-1"
          :style="{ color: promptColor }"
        />
      </Button>
    </div>
  </div>
</template>


<style>
.answerButton:hover {
  transform: scale(1.03);
  transition: transform 0.3s ease-in-out;
}

/* Only show focus outline for keyboard navigation */
#question-layer button:focus-visible,
#question-layer .answerButton:focus-visible {
  outline: 2px solid #3B82F6 !important;
  outline-offset: 2px !important;
}

/* Remove default focus outline for mouse users */
#question-layer button:focus:not(:focus-visible),
#question-layer .answerButton:focus:not(:focus-visible) {
  outline: none;
}

/* Special styles for star rating buttons on keyboard focus */
#question-layer button:has(.hugeicons):focus-visible {
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 0.25rem;
}

/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
